import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/AuthService';
import { userApiClient } from '../../userApiClient';
import { ParticipantDetails } from '../../models/RoomBooking';
import NumberInput from '../NumberInput';
import BookingSignInPrompt from './BookingSignInPrompt';

interface WhoIsComingStepProps {
  onNext: () => void;
  onBack: () => void;
  onParticipantDetailsChange: (details: ParticipantDetails) => void;
  onSignIn?: () => void;
}

const WhoIsComingStep: React.FC<WhoIsComingStepProps> = ({
  onNext,
  onBack,
  onParticipantDetailsChange,
  onSignIn,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(authService.getIsLoggedIn());
  const [formData, setFormData] = useState<ParticipantDetails>({
    bandName: '',
    participantCount: 1,
    details: '',
  });
  const [error, setError] = useState('');
  const profileFetchGenerationRef = useRef(0);

  useEffect(() => {
    onParticipantDetailsChange(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check auth state periodically
    const interval = setInterval(() => {
      setIsLoggedIn(authService.getIsLoggedIn());
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Prefill band name from profile once when logged in. Do not depend on
  // onParticipantDetailsChange: an unstable callback from the parent would re-run
  // this effect on every parent render and overwrite the field while the user types.
  useEffect(() => {
    if (!isLoggedIn) return;

    const gen = ++profileFetchGenerationRef.current;
    const fetchProfile = async () => {
      try {
        const profile = await userApiClient.getProfile();
        if (gen !== profileFetchGenerationRef.current) return;

        setFormData(prev => {
          // Late response: never overwrite text the user already entered
          if (prev.bandName !== '') {
            return prev;
          }
          const newData = { ...prev, bandName: profile.bandName || '' };
          onParticipantDetailsChange(newData);
          return newData;
        });
      } catch (error) {
        console.error('Error getting profile:', error);
      }
    };

    fetchProfile();
    return () => {
      profileFetchGenerationRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only prefill on login; callback identity must not retrigger fetch
  }, [isLoggedIn]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      onParticipantDetailsChange(newData);
      return newData;
    });
  };



  const goToLogin = () => {
    if (onSignIn) {
      onSignIn();
      return;
    }
    navigate('/login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isLoggedIn) {
      goToLogin();
      return;
    }

    // Save the participant details
    try {
      localStorage.setItem('participantDetails', JSON.stringify(formData));
      onNext();
    } catch (err) {
      setError(t('bookingSteps.saveError'));
      console.error('Save error:', err);
    }
  };

  if (!isLoggedIn) {
    return <BookingSignInPrompt onContinue={goToLogin} />;
  }

  return (
    <Box sx={{ mt: 2, maxWidth: 800, mx: 'auto', width: '100%' }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('bookingSteps.whoIsComing')}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={`${t('bookingSteps.bandArtistName')} (${t('search.optional', 'Optional')})`}
                name="bandName"
                value={formData.bandName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <NumberInput
                value={formData.participantCount}
                label={t('bookingSteps.participantCount')}
                onValueChange={(value) => {
                  setFormData(prev => {
                    const newData = {
                      ...prev,
                      participantCount: value
                    };
                    onParticipantDetailsChange(newData);
                    return newData;
                  });
                }}
                min={1}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('bookingSteps.additionalDetails')}
                name="details"
                value={formData.details}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default WhoIsComingStep; 